/**
 * Detecta no boot, ANTES do React montar, se a sessão persistida do
 * supabase-js já está expirada. Se sim, executa `tearDownLocalSession`
 * imediatamente para garantir que:
 *
 *  - rotas protegidas nunca cheguem a montar com sessão morta;
 *  - o redirect para `/` acontece SEM depender da resposta da rede
 *    (cobre o caso de refresh travado / hang / offline);
 *  - o banner `session-expired-notice` aparece consistentemente.
 *
 * Sem isso, em modo "hang" (refresh nunca completa), o auto-refresh do
 * supabase-js fica pendurado e a UI protegida vaza enquanto espera.
 */
import { tearDownLocalSession } from "./teardown";

const isExpired = (expiresAt: unknown): boolean => {
  if (typeof expiresAt !== "number" || !Number.isFinite(expiresAt)) return false;
  // expires_at é UNIX seconds. Damos 5s de folga para clock skew.
  const nowSec = Math.floor(Date.now() / 1000);
  return expiresAt <= nowSec + 5;
};

export const detectExpiredSessionAtBoot = (): void => {
  if (typeof window === "undefined") return;
  try {
    const ls = window.localStorage;
    for (let i = 0; i < ls.length; i++) {
      const key = ls.key(i);
      if (!key || !key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;
      const raw = ls.getItem(key);
      if (!raw) continue;
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        continue;
      }
      const session =
        parsed && typeof parsed === "object" && "currentSession" in parsed
          ? (parsed as { currentSession?: { expires_at?: unknown; refresh_token?: unknown } }).currentSession
          : (parsed as { expires_at?: unknown; refresh_token?: unknown });

      // Só derruba se o access token expirou E não há refresh_token
      // aproveitável. Caso contrário, deixamos o supabase-js tentar
      // renovar — refresh tokens vivem por semanas, então mesmo após 23h
      // o usuário deve permanecer logado sem re-login.
      const hasRefreshToken =
        !!session &&
        typeof (session as { refresh_token?: unknown }).refresh_token === "string" &&
        ((session as { refresh_token: string }).refresh_token.length > 0);

      if (session && isExpired(session.expires_at) && !hasRefreshToken) {
        void tearDownLocalSession("refresh_failed");
        return;
      }
    }
  } catch {
    /* noop */
  }
};

